// Creating a company from the console (WEB_PLAN.md §5.2 "Create a company"), the pure parts. Ports of
// NoCompanyView.create / SyncCenter.createCompany (GroundTeam/Sync/SyncCenter.swift:959) where the console does the
// same thing, and of the rules the plan adds because the server creates no items:
// - the company name as the server will keep it (`public.clean_company_name`), and the admin's name and the company's
//   currency/distance defaults from the account and the browser (iOS: Apple's shared name, the device locale);
// - the seed (domain/seed `companySeedPatches`) as ONE `sync_push` batch in push order, with the envelope a brand-new
//   item gets: base_version null, modified_by = this browser's device id, modified_at in server time with ms;
// - what is left to send after an answer. Items whose answer got lost come back as conflicts on the retry (they landed),
//   so a retry never duplicates anything: the ids are fixed when the batch is built;
// - when to try again by itself, and the memberships that prove a create or a join went through although its answer
//   never arrived (iOS adopts the company then instead of failing).

import { isNetworkError, OFFLINE_TEXT, ServerError, serverErrorKind } from '@/auth/errors';
import { parseMemberships } from '@/auth/memberships';
import { SETTINGS_ID } from '@/contracts/collections';
import type { DistanceUnit } from '@/contracts/enums';
import { isUuid, sameId } from '@/contracts/uuid';
import type { Membership, PushError, PushItem, PushResult } from '@/contracts/wire';
import { companySeedPatches } from '@/domain/seed';
import { describeSyncError, isSyncApiError } from '@/sync/api';
import { canonicalPayload, isJsonObject } from '@/sync/canonical';
import { assertJsonPayload, assertWireValues } from '@/sync/outbox';
import type { Patch } from '@/sync/patch';
import { PUSH_BATCH_BYTES, PUSH_BATCH_LIMIT, pushOrder, utf8Length } from '@/sync/push';

// MARK: - Company name and the admin's profile

/** `clean_company_name` keeps at most this many characters. */
export const COMPANY_NAME_MAX = 80;

// The three passes of public.clean_company_name (self_signup.sql:185), in its order: drop invisible format characters,
// turn control characters into spaces, collapse every run of spaces into one; then trim, cut to 80, trim again.
const INVISIBLE = /[\u200B\u200E\u200F\u061C\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/g;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001F\u007F-\u009F\u2028\u2029]/g;
const SPACES = /[\s\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]+/g;

/** The company name as the server will store it; '' when nothing visible is left (the server says invalid_input). */
export function cleanCompanyName(raw: string): string {
  const oneLine = raw.replace(INVISIBLE, '').replace(CONTROL, ' ').replace(SPACES, ' ').trim();
  return Array.from(oneLine).slice(0, COMPANY_NAME_MAX).join('').trim();
}

export interface ProfileName {
  firstName: string;
  lastName: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The name to prefill for the admin's profile: the name Sign in with Apple shared (only on the first consent, iOS
 * `pendingProfileName`), else the provider's name claims in the Supabase `user_metadata` (Google sends given/family
 * names and a full name), else nothing (the person types it; the first name is required, like iOS).
 */
export function accountProfileName(
  metadata: unknown,
  shared: { first: string; last: string } | null | undefined,
): ProfileName {
  const clean = (v: unknown) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');
  if (shared && (clean(shared.first) || clean(shared.last))) {
    return { firstName: clean(shared.first), lastName: clean(shared.last) };
  }
  const m = isRecord(metadata) ? metadata : {};
  const given = clean(m.given_name) || clean(m.first_name);
  const family = clean(m.family_name) || clean(m.last_name);
  if (given || family) return { firstName: given, lastName: family };
  const full = clean(m.full_name) || clean(m.name);
  // An email address in a name claim is not a name.
  if (!full || full.includes('@')) return { firstName: '', lastName: '' };
  const space = full.indexOf(' ');
  return space < 0 ? { firstName: full, lastName: '' } : { firstName: full.slice(0, space), lastName: full.slice(space + 1) };
}

/** Regions whose measurement system is US customary (iOS `Locale.measurementSystem == .us` → miles). */
const MILES_REGIONS = new Set(['US', 'LR', 'MM']);

const EURO_REGIONS = ['AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK'];

/** The currency of a region (iOS `Locale.current.currency`), for the regions the app is likely to meet. */
const REGION_CURRENCY: Readonly<Record<string, string>> = {
  ...Object.fromEntries(EURO_REGIONS.map((r) => [r, 'EUR'])),
  US: 'USD', PR: 'USD', CA: 'CAD', MX: 'MXN', GB: 'GBP', AU: 'AUD', NZ: 'NZD', IN: 'INR', JP: 'JPY', CN: 'CNY',
  KR: 'KRW', BR: 'BRL', ZA: 'ZAR', CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF',
  RO: 'RON', IL: 'ILS', AE: 'AED', SA: 'SAR', SG: 'SGD', HK: 'HKD', TW: 'TWD', PH: 'PHP', TR: 'TRY', NG: 'NGN',
  KE: 'KES', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN',
};

/**
 * The new company's currency and distance unit from the browser's language, like `completeOnboarding` takes them
 * from the iPhone's locale (`OnboardingCurrencies.deviceDefault`, `measurementSystem == .us ? .miles : .kilometers`).
 * An unknown region keeps CompanySettings' defaults (USD, miles).
 */
export function localeDefaults(locale: string | null | undefined): { currencyCode: string; distanceUnit: DistanceUnit } {
  let region: string | undefined;
  try {
    region = locale ? new Intl.Locale(locale).maximize().region?.toUpperCase() : undefined;
  } catch {
    region = undefined;
  }
  if (!region) return { currencyCode: 'USD', distanceUnit: 'miles' };
  return {
    currencyCode: REGION_CURRENCY[region] ?? 'USD',
    distanceUnit: MILES_REGIONS.has(region) ? 'miles' : 'kilometers',
  };
}

// MARK: - Memberships after create / accept

/**
 * The server answered with something this build can't read. For create_company / accept_invitation that answer was a
 * success (refusals come back as errors), so it counts as an unknown outcome: `my_memberships` decides.
 */
export class UnexpectedAnswerError extends Error {
  constructor(what: string) {
    super("The team server sent an answer Ground Team couldn't read. Try again.");
    this.name = 'UnexpectedAnswerError';
    this.cause = what;
  }
}

/** `create_company` / `accept_invitation` answer one `membership_json`; decoded like `my_memberships`. */
export function membershipFromJson(json: unknown): Membership | null {
  return parseMemberships(Array.isArray(json) ? json : [json])[0] ?? null;
}

/** The company this account created with `profileId` (active, admin), if the server has it. */
export function findCreatedCompany(list: readonly Membership[], profileId: string): Membership | null {
  return list.find((m) => m.status === 'active' && m.role === 'admin' && sameId(m.profile_id, profileId)) ?? null;
}

/** The account's active membership in `companyId`, if any. */
export function findJoinedCompany(list: readonly Membership[], companyId: string): Membership | null {
  return list.find((m) => m.status === 'active' && sameId(m.company_id, companyId)) ?? null;
}

/**
 * No answer, the server was overloaded (iOS `isRetryable`), or an answer we couldn't read: the call may have gone
 * through.
 */
export function outcomeUnknown(e: unknown): boolean {
  if (isNetworkError(e) || e instanceof UnexpectedAnswerError) return true;
  return e instanceof ServerError && !e.refused && (e.status >= 500 || e.status === 429);
}

/** `create_company` failed in a way that may hide a company created anyway (SyncCenter.createCompany's catch). */
export function mayHaveCreated(e: unknown): boolean {
  return outcomeUnknown(e) || serverErrorKind(e) === 'already_member';
}

/** `accept_invitation` failed in a way that may hide a join that went through (SyncCenter.isJoinConflict). */
export function mayHaveJoined(e: unknown): boolean {
  const kind = serverErrorKind(e);
  return outcomeUnknown(e) || kind === 'no_invitation' || kind === 'already_member';
}

// MARK: - The seed batch

export interface SeedEnvelope {
  /** Server-clock edit time with milliseconds (`toEnvelopeDate(nowForEnvelope())`). */
  modifiedAt: string;
  /** This browser's `web-<uuid>` (sync/replica `browserDeviceId`). */
  modifiedBy: string;
}

/**
 * The `sync_push` items for a list of create patches, in push order (saves, then the collection order, then id:
 * settings and the admin's profile first). Every payload goes through the same checks and normalisation as
 * `useSave()` (plain JSON, no null keys, whole-second dates, local-only keys stripped), and the batch must fit one call.
 */
export function seedPushItems(patches: readonly Patch[], envelope: SeedEnvelope): PushItem[] {
  if (!/^web-.+/.test(envelope.modifiedBy) || envelope.modifiedBy.length > 256) {
    throw new TypeError(`modified_by must be this browser's web-<uuid>, got "${envelope.modifiedBy}".`);
  }
  if (!Number.isFinite(Date.parse(envelope.modifiedAt))) {
    throw new TypeError(`modified_at is not a date: "${envelope.modifiedAt}".`);
  }
  const items: PushItem[] = [];
  const seen = new Set<string>();
  for (const p of patches) {
    if (p.kind !== 'create') throw new TypeError(`A company seed only creates items (got ${p.kind} ${p.collection}).`);
    if (!isUuid(p.id)) throw new TypeError(`${p.collection} id "${p.id}" is not a UUID.`);
    if (p.collection === 'settings' ? !sameId(p.id, SETTINGS_ID) : !sameId(String(p.payload.id ?? ''), p.id)) {
      throw new TypeError(`${p.collection} ${p.id}: the payload id must be the item id.`);
    }
    const key = `${p.collection}/${p.id.toLowerCase()}`;
    if (seen.has(key)) throw new TypeError(`${p.collection} ${p.id} is in the seed twice.`);
    seen.add(key);
    const payload = canonicalPayload(p.collection, assertJsonPayload(structuredClone(p.payload)));
    assertWireValues(p.collection, payload, null);
    items.push({
      collection: p.collection,
      id: p.id,
      payload,
      modified_at: envelope.modifiedAt,
      modified_by: envelope.modifiedBy,
      deleted: false,
      base_version: null,
    });
  }
  const ordered = pushOrder(items.map((item) => ({ collection: item.collection, id: item.id, kind: 'save' as const, item })))
    .map((e) => e.item);
  const bytes = ordered.reduce((sum, item) => sum + utf8Length(JSON.stringify(item.payload)), 0);
  if (ordered.length > PUSH_BATCH_LIMIT || bytes > PUSH_BATCH_BYTES) {
    throw new RangeError(`The company seed must fit one sync_push call (${ordered.length} items, ${bytes} bytes).`);
  }
  return ordered;
}

export interface CompanySeedRequest {
  /** The `company_name` create_company returned (the typed name if it sent none). */
  companyName: string;
  /** The `p_profile_id` sent to create_company: the admin's `users` item id. */
  profileId: string;
  firstName: string;
  lastName: string;
  /** The account's email ('' for a phone-only account). */
  email: string;
  /** The account's phone as shown to people ('' for none). */
  phone: string;
  /** The browser's language (navigator.language) for the currency and distance defaults. */
  locale?: string | null;
  now?: Date;
}

/** The whole seed of a new company as one push batch (see `seedPushItems`). */
export function companySeedItems(req: CompanySeedRequest, envelope: SeedEnvelope): PushItem[] {
  const { currencyCode, distanceUnit } = localeDefaults(req.locale);
  const patches = companySeedPatches({
    companyName: req.companyName,
    admin: {
      profileId: req.profileId,
      firstName: req.firstName,
      lastName: req.lastName,
      email: req.email,
      phone: req.phone,
    },
    currencyCode,
    distanceUnit,
    now: req.now,
  });
  return seedPushItems(patches, envelope);
}

export interface SeedSettlement {
  /** Items still to send, in push order (a recreated tombstone carries the version it replaces). */
  remaining: PushItem[];
  /** Items the server refused (`invalid` / `forbidden`); they are in `remaining` too, for "Try again". */
  refused: PushError[];
  /** Items now on the server (saved, or already there from an answer that got lost). */
  landed: number;
}

/**
 * What is left after a `sync_push` answer. Saved items are done. A conflict on a brand-new id means the item is on the
 * server already (an earlier call landed but its answer didn't arrive): done too, unless the server copy is a
 * tombstone, which is recreated over that version (a company must not lose its settings). Refused and unanswered items
 * stay.
 */
export function settleSeedPush(sent: readonly PushItem[], result: PushResult): SeedSettlement {
  const key = (collection: string | null, id: string) => `${collection ?? ''}/${id.toLowerCase()}`;
  const saved = new Set(result.saved.map((s) => key(s.collection, s.id)));
  const conflicts = new Map(result.conflicts.map((row) => [key(row.collection, row.id), row]));
  const refusedKeys = new Set(result.errors.map((e) => key(e.collection, e.id)));
  const remaining: PushItem[] = [];
  const refused: PushError[] = [];
  let landed = 0;
  for (const item of sent) {
    const k = key(item.collection, item.id);
    if (saved.has(k)) {
      landed += 1;
      continue;
    }
    const conflict = conflicts.get(k);
    if (conflict && !conflict.deleted) {
      landed += 1;
      continue;
    }
    if (conflict) {
      remaining.push({ ...item, base_version: conflict.version });
      continue;
    }
    if (refusedKeys.has(k)) {
      const error = result.errors.find((e) => key(e.collection, e.id) === k);
      if (error) refused.push(error);
    }
    remaining.push(item);
  }
  return { remaining, refused, landed };
}

// MARK: - Retrying

/** Waits between automatic tries; after the last one the screen offers Try again. */
export const SEED_RETRY_DELAYS_MS: readonly number[] = [1000, 2000, 4000, 8000, 15000];

/** The wait before automatic try `attempt + 1` (0-based), or null when it's the person's turn. */
export function seedRetryDelay(attempt: number): number | null {
  return attempt >= 0 && attempt < SEED_RETRY_DELAYS_MS.length ? SEED_RETRY_DELAYS_MS[attempt]! : null;
}

export interface SetupProblem {
  /** What the person reads when it's their turn (the automatic tries stopped, or never applied): ends with Try again. */
  text: string;
  /** What shows while the next automatic try is coming ("… Trying again…"). */
  retryingText: string;
  /** Worth trying again by itself (no answer, server busy, part of the batch still missing). */
  autoRetry: boolean;
  /** The browser is offline: the next try waits for the connection. */
  waitForOnline: boolean;
}

function stopped(text: string): SetupProblem {
  return { text, retryingText: text, autoRetry: false, waitForOnline: false };
}

/** A whole `sync_push` call failed (sync/api's SyncApiError, or anything thrown on the way). */
export function setupProblemFromError(e: unknown, online: boolean): SetupProblem {
  if (isSyncApiError(e)) {
    if (e.kind === 'network') {
      return online
        ? {
            text: "Couldn't reach the team server. Check your connection, then try again.",
            retryingText: "Couldn't reach the team server. Trying again…",
            autoRetry: true,
            waitForOnline: false,
          }
        : {
            text: OFFLINE_TEXT,
            retryingText: "You're offline. Setup finishes when you're back online.",
            autoRetry: true,
            waitForOnline: true,
          };
    }
    if (e.retryable) {
      return {
        text: "The team server didn't answer. Try again in a moment.",
        retryingText: "The team server didn't answer. Trying again…",
        autoRetry: true,
        waitForOnline: false,
      };
    }
    return stopped(describeSyncError(e));
  }
  return stopped(e instanceof Error && e.message.trim() ? e.message.trim() : 'Something went wrong. Please try again.');
}

/** Part of the batch is not on the server yet. Refusals won't change by themselves, so they wait for Try again. */
export function setupProblemFromSettlement(settlement: SeedSettlement): SetupProblem {
  if (settlement.refused.length === 0) {
    return {
      text: "Some of the company's defaults aren't saved yet. Try again.",
      retryingText: "Some of the company's defaults aren't saved yet. Trying again…",
      autoRetry: true,
      waitForOnline: false,
    };
  }
  const first = settlement.refused[0]!;
  const detail = first.message.trim() && first.message !== first.code ? ` (${first.message.trim()})` : '';
  const what = settlement.refused.length === 1 ? 'One of the company\'s defaults wasn\'t' : `${settlement.refused.length} of the company's defaults weren't`;
  return stopped(`${what} accepted by the team server${detail}.`);
}

// MARK: - A setup that has to finish later

/**
 * A company setup whose seed is not all on the server yet, kept in localStorage so it can be finished after a reload
 * (the console opens the company as soon as `my_memberships` lists it, so /welcome never sees it again). It is stored
 * BEFORE `create_company` is called: if that answer never arrives (no connection, the tab closed), the company may
 * exist anyway, and the console finds it by the admin's profile id, like iOS keeps `pendingCreateProfileId`.
 */
export interface PendingSetup {
  version: 1;
  /** The Supabase user who created it (another account signing in on this browser never touches it). */
  authUserId: string;
  /** `p_profile_id`: the admin's `users` item id, and how a company whose create answer got lost is recognised. */
  profileId: string;
  /** Lower-case company id (`p_company`); null until `create_company` answered. */
  companyId: string | null;
  companyName: string;
  /** What is still to send, as built (fixed ids, envelopes and payloads). */
  items: PushItem[];
}

/** A setup whose company is known: what `sync_push` needs. */
export type CompanyPendingSetup = PendingSetup & { companyId: string };

export function encodePendingSetup(setup: PendingSetup): string {
  return JSON.stringify(setup);
}

/** The stored setup if it is well-formed and belongs to `authUserId`; null otherwise. */
export function decodePendingSetup(textValue: string | null | undefined, authUserId: string): PendingSetup | null {
  if (!textValue) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(textValue);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.version !== 1) return null;
  const { companyId, companyName, items, profileId } = parsed;
  if (typeof parsed.authUserId !== 'string' || !sameId(parsed.authUserId, authUserId)) return null;
  if (typeof profileId !== 'string' || !isUuid(profileId)) return null;
  if (companyId !== null && (typeof companyId !== 'string' || !isUuid(companyId))) return null;
  if (typeof companyName !== 'string' || !Array.isArray(items)) return null;
  const valid = items.every(
    (i) => isRecord(i) && typeof i.collection === 'string' && typeof i.id === 'string' && isJsonObject(i.payload)
      && typeof i.modified_at === 'string' && typeof i.modified_by === 'string',
  );
  if (!valid) return null;
  return {
    version: 1,
    authUserId: parsed.authUserId,
    profileId,
    companyId: companyId === null ? null : companyId.toLowerCase(),
    companyName,
    items: items as PushItem[],
  };
}

/**
 * The stored setup as it applies to the company the console just opened, or null when it is another company's. A
 * setup whose create answer never arrived (no company id) is this company's when the membership's profile is the one
 * it was created with (`findCreatedCompany`'s rule).
 */
export function pendingSetupForCompany(
  setup: PendingSetup | null,
  session: { authUserId: string; companyId: string; profileId: string },
): CompanyPendingSetup | null {
  if (!setup || !sameId(setup.authUserId, session.authUserId)) return null;
  const companyId = session.companyId.toLowerCase();
  if (setup.companyId !== null) return setup.companyId === companyId ? { ...setup, companyId } : null;
  return sameId(setup.profileId, session.profileId) ? { ...setup, companyId } : null;
}

/**
 * On /welcome the account is in no company, so a stored setup for a known company is stale (the company was left or
 * deleted). One without a company id is kept: its create may still have gone through, and creating again reuses its
 * profile id so that company is found instead of a second one failing with already_member.
 */
export function isStaleOnWelcome(setup: PendingSetup | null): boolean {
  return setup !== null && setup.companyId !== null;
}
